import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ChatMessage } from '@/types/evolve';
import { toast } from 'sonner';

const MESSAGES_PER_PAGE = 50;

export function useChatHistory(userId: string | undefined) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [oldestTimestamp, setOldestTimestamp] = useState<string | null>(null);

  // Fetch initial messages (most recent)
  const fetchInitialMessages = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(MESSAGES_PER_PAGE);

      if (error) throw error;

      // Reverse to show oldest first in the UI
      const reversedData = (data || []).reverse();
      
      const mappedMessages: ChatMessage[] = reversedData.map(msg => ({
        id: msg.id,
        text: msg.text,
        isAI: msg.is_ai,
        createdAt: new Date(msg.created_at),
      }));

      setMessages(mappedMessages);
      setHasMore((data || []).length === MESSAGES_PER_PAGE);
      
      if (reversedData.length > 0) {
        setOldestTimestamp(reversedData[0].created_at);
      }
    } catch (error) {
      console.error('Error fetching chat history:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Load older messages (when scrolling up)
  const loadOlderMessages = useCallback(async () => {
    if (!userId || !oldestTimestamp || !hasMore) return;

    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('user_id', userId)
        .lt('created_at', oldestTimestamp)
        .order('created_at', { ascending: false })
        .limit(MESSAGES_PER_PAGE);

      if (error) throw error;

      // Reverse to maintain chronological order
      const reversedData = (data || []).reverse();
      
      const olderMessages: ChatMessage[] = reversedData.map(msg => ({
        id: msg.id,
        text: msg.text,
        isAI: msg.is_ai,
        createdAt: new Date(msg.created_at),
      }));

      // Prepend older messages
      setMessages(prev => [...olderMessages, ...prev]);
      setHasMore((data || []).length === MESSAGES_PER_PAGE);
      
      if (reversedData.length > 0) {
        setOldestTimestamp(reversedData[0].created_at);
      }
    } catch (error) {
      console.error('Error loading older messages:', error);
    }
  }, [userId, oldestTimestamp, hasMore]);

  // Load initial messages
  useEffect(() => {
    fetchInitialMessages();
  }, [userId, fetchInitialMessages]);

  // Save a new message
  const saveMessage = useCallback(async (text: string, isAI: boolean) => {
    if (!userId) return null;

    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .insert({
          user_id: userId,
          text,
          is_ai: isAI,
        })
        .select()
        .single();

      if (error) throw error;

      const newMessage: ChatMessage = {
        id: data.id,
        text: data.text,
        isAI: data.is_ai,
        createdAt: new Date(data.created_at),
      };

      setMessages(prev => [...prev, newMessage]);
      return newMessage;
    } catch (error) {
      console.error('Error saving message:', error);
      return null;
    }
  }, [userId]);

  // Update a message (for streaming AI responses)
  const updateMessage = useCallback(async (id: string, text: string) => {
    if (!userId) return;

    try {
      await supabase
        .from('chat_messages')
        .update({ text })
        .eq('id', id);

      setMessages(prev => prev.map(msg => 
        msg.id === id ? { ...msg, text } : msg
      ));
    } catch (error) {
      console.error('Error updating message:', error);
    }
  }, [userId]);

  // Search messages
  const searchMessages = useCallback(async (query: string) => {
    if (!userId) return [];

    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('user_id', userId)
        .ilike('text', `%${query}%`)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      return (data || []).map(msg => ({
        id: msg.id,
        text: msg.text,
        isAI: msg.is_ai,
        createdAt: new Date(msg.created_at),
      }));
    } catch (error) {
      console.error('Error searching messages:', error);
      return [];
    }
  }, [userId]);

  // Load more messages (older ones when scrolling up)
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await loadOlderMessages();
  }, [hasMore, loading, loadOlderMessages]);

  // Clear local state (for new sessions)
  const clearMessages = useCallback(() => {
    setMessages([]);
    setHasMore(true);
  }, []);

  // Add message locally (for optimistic updates)
  const addMessageLocally = useCallback((message: ChatMessage) => {
    setMessages(prev => [...prev, message]);
  }, []);

  return {
    messages,
    setMessages,
    loading,
    hasMore,
    saveMessage,
    updateMessage,
    searchMessages,
    loadMore,
    clearMessages,
    addMessageLocally,
    refetch: fetchInitialMessages,
  };
}
